/**
 * The small, explicit set of cases the static viewer can show, and where
 * each one's pre-generated bundle is served from. Mirrors
 * `apps/runner/src/case-registry.ts`'s `CASE_REGISTRY` in spirit (a fixed
 * table, not something a URL can expand) but is independent of it: this
 * app never calls the runner, it only fetches whichever static bundle
 * directory a case's `baseUrl` names.
 *
 * Adding a case here is a deliberate step after its bundle has actually
 * been generated and committed under `apps/web/public/<baseUrl>/bundle/`
 * -- an entry with no matching bundle on disk just shows the same honest
 * `ARTIFACT_MISSING` error state a 404 for the demo case already does.
 */
export interface KnownCase {
  caseId: string;
  /** Short label for nav/switcher UI. */
  label: string;
  /** Passed to `loadEvidenceBundle`'s `baseUrl` option. */
  baseUrl: string;
  /** Reviewed counterparty wallet eligible for a browser-side Anchor read. Absent means Anchor must not be called. */
  anchorWallet?: `0x${string}`;
}

export const KNOWN_CASES: readonly KnownCase[] = [
  { caseId: "demo-reentrancy", label: "Reentrancy (demo)", baseUrl: "/demo" },
  {
    caseId: "temple-stax-migratestake-2022",
    label: "TempleDAO/STAX (real, 2022)",
    baseUrl: "/stax",
    anchorWallet: "0x9c9fb3100a2a521985f0c47de3b4598dafd25b01",
  },
];

export const DEFAULT_CASE_ID = KNOWN_CASES[0]?.caseId ?? "demo-reentrancy";

/** `undefined` for a `caseId` this viewer doesn't have a known bundle location for. */
export function baseUrlForCase(caseId: string): string | undefined {
  return KNOWN_CASES.find((entry) => entry.caseId === caseId)?.baseUrl;
}

/** Returns only explicitly reviewed wallets; arbitrary case IDs never become Anchor queries. */
export function anchorWalletForCase(caseId: string): `0x${string}` | undefined {
  return KNOWN_CASES.find((entry) => entry.caseId === caseId)?.anchorWallet;
}

/** Security boundary for browser-side Anchor reads: only explicitly reviewed wallets pass. */
export function isReviewedAnchorWallet(wallet: string): boolean {
  const normalizedWallet = wallet.toLowerCase();
  return KNOWN_CASES.some((entry) => entry.anchorWallet?.toLowerCase() === normalizedWallet);
}
